import { useCallback, useEffect, useState } from 'react';
import {
  searchintelApi,
  type KeywordIntel,
  type PortfolioSearchCoverage,
  type RankingRecord,
  type SearchDashboard,
  type SearchQueryRecord,
  type SearchRecommendation,
  type SearchTrend,
} from '../../api/searchintel';

type Tab = 'overview' | 'queries' | 'keywords' | 'rankings' | 'trends' | 'portfolio' | 'recommendations';

function trendClass(pct: number) {
  if (pct >= 15) return 'text-emerald-700 bg-emerald-50';
  if (pct <= -5) return 'text-red-700 bg-red-50';
  return 'text-slate-600 bg-slate-50';
}

function rankTrendClass(t: string) {
  if (t === 'up' || t === 'recovered') return 'text-emerald-700 bg-emerald-50';
  if (t === 'down') return 'text-red-700 bg-red-50';
  return 'text-sky-700 bg-sky-50';
}

function kwCategoryClass(c: string) {
  if (c === 'high_opportunity' || c === 'growing') return 'text-emerald-700 bg-emerald-50';
  if (c === 'missing') return 'text-violet-700 bg-violet-50';
  if (c === 'declining') return 'text-red-700 bg-red-50';
  return 'text-slate-600 bg-slate-50';
}

export function SearchIntelPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SearchDashboard | null>(null);
  const [queries, setQueries] = useState<SearchQueryRecord[]>([]);
  const [keywords, setKeywords] = useState<KeywordIntel[]>([]);
  const [rankings, setRankings] = useState<RankingRecord[]>([]);
  const [trends, setTrends] = useState<SearchTrend[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioSearchCoverage | null>(null);
  const [recommendations, setRecommendations] = useState<SearchRecommendation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, q, kw, rk, tr, port, recs] = await Promise.all([
        searchintelApi.dashboard(),
        searchintelApi.listQueries(),
        searchintelApi.listKeywords(),
        searchintelApi.listRankings(),
        searchintelApi.listTrends(),
        searchintelApi.portfolioCoverage(),
        searchintelApi.listRecommendations(),
      ]);
      setDashboard(dash);
      setQueries(q);
      setKeywords(kw);
      setRankings(rk);
      setTrends(tr);
      setPortfolio(port);
      setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load search intelligence data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try { await searchintelApi.generateRecommendations(); await refresh(); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Search Dashboard' },
    { id: 'queries', label: 'Query Performance' },
    { id: 'keywords', label: 'Keyword Explorer' },
    { id: 'rankings', label: 'Organic Rankings' },
    { id: 'trends', label: 'Search Trends' },
    { id: 'portfolio', label: 'Portfolio Coverage' },
    { id: 'recommendations', label: 'Recommendation Workspace' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Amazon Search Intelligence</h1>
          <p className="text-sm text-slate-500">Brand Analytics, Search Query Performance &amp; search intelligence — FRP-00001</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">
          Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Queries Tracked', value: dashboard.total_queries_tracked.toString() },
              { label: 'Avg Click Share', value: `${dashboard.avg_click_share_pct.toFixed(1)}%` },
              { label: 'Brand Visibility', value: `${dashboard.brand_visibility_pct.toFixed(0)}%` },
              { label: 'High Opportunity KWs', value: dashboard.high_opportunity_keywords.toString() },
              { label: 'Missing Keywords', value: dashboard.missing_keywords.toString() },
              { label: 'Growing Keywords', value: dashboard.growing_keywords.toString() },
              { label: 'Lost Rankings', value: dashboard.lost_rankings.toString() },
              { label: 'Recovered Rankings', value: dashboard.recovered_rankings.toString() },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>
          {dashboard.datasets_available && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase text-slate-500">Connected Datasets</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {dashboard.datasets_available.map((d) => (
                  <span key={d} className="rounded bg-emerald-50 px-2 py-1 text-xs text-emerald-700">{d.replace(/_/g, ' ')}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'queries' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Query</th>
                <th className="px-4 py-2">Volume</th>
                <th className="px-4 py-2">Trend</th>
                <th className="px-4 py-2">Click Share</th>
                <th className="px-4 py-2">Conv Share</th>
                <th className="px-4 py-2">Organic Pos</th>
                <th className="px-4 py-2">Intent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {queries.map((q) => (
                <tr key={q.id}>
                  <td className="px-4 py-2 font-medium">{q.query}</td>
                  <td className="px-4 py-2">{q.search_volume.toLocaleString()}</td>
                  <td className="px-4 py-2"><span className={`rounded px-2 py-0.5 text-xs ${trendClass(q.volume_trend_pct)}`}>{q.volume_trend_pct > 0 ? '+' : ''}{q.volume_trend_pct}%</span></td>
                  <td className="px-4 py-2">{q.click_share_pct.toFixed(1)}%</td>
                  <td className="px-4 py-2">{q.conversion_share_pct.toFixed(1)}%</td>
                  <td className="px-4 py-2">{q.organic_position?.toFixed(0) ?? '—'}</td>
                  <td className="px-4 py-2 capitalize text-slate-500">{q.intent?.replace(/_/g, ' ') ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'keywords' && (
        <div className="space-y-3">
          {keywords.map((k) => (
            <div key={k.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <span className={`rounded px-2 py-0.5 text-xs capitalize ${kwCategoryClass(k.category)}`}>{k.category.replace(/_/g, ' ')}</span>
                  <h3 className="font-medium text-slate-900 mt-1">{k.keyword}</h3>
                  <p className="text-sm text-slate-500">{k.search_volume.toLocaleString()} vol · Click {k.click_share_pct.toFixed(1)}% · Rank {k.organic_rank?.toFixed(0) ?? 'N/A'}</p>
                </div>
                <span className={`rounded px-2 py-0.5 text-xs ${trendClass(k.volume_trend_pct)}`}>{k.volume_trend_pct > 0 ? '+' : ''}{k.volume_trend_pct}%</span>
              </div>
              {k.coverage_gap && <p className="text-xs text-violet-600 mt-2">Coverage gap — not fully addressed in listings</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'rankings' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">SKU</th>
                <th className="px-4 py-2">Keyword</th>
                <th className="px-4 py-2">Rank</th>
                <th className="px-4 py-2">Trend</th>
                <th className="px-4 py-2">Velocity</th>
                <th className="px-4 py-2">Stability</th>
                <th className="px-4 py-2">Days</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rankings.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2 font-medium">{r.sku}</td>
                  <td className="px-4 py-2 text-slate-600">{r.keyword}</td>
                  <td className="px-4 py-2 font-semibold">{r.organic_rank.toFixed(0)}</td>
                  <td className="px-4 py-2"><span className={`rounded px-2 py-0.5 text-xs capitalize ${rankTrendClass(r.rank_trend ?? 'stable')}`}>{r.rank_trend ?? 'stable'}</span></td>
                  <td className="px-4 py-2">{r.rank_velocity > 0 ? '+' : ''}{r.rank_velocity.toFixed(1)}</td>
                  <td className="px-4 py-2">{(r.rank_stability_score * 100).toFixed(0)}%</td>
                  <td className="px-4 py-2">{r.days_at_rank}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'trends' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {trends.map((t) => (
            <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start">
                <h3 className="font-medium text-slate-900">{t.query_or_topic}</h3>
                <span className={`rounded px-2 py-0.5 text-xs font-medium ${trendClass(t.trend_pct)}`}>+{t.trend_pct}%</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">{t.category} · {t.volume.toLocaleString()} volume</p>
              <div className="mt-2 flex gap-2 text-xs">
                {t.intent && <span className="rounded bg-slate-100 px-2 py-0.5 capitalize">{t.intent.replace(/_/g, ' ')}</span>}
                {t.seasonality && <span className="rounded bg-amber-50 text-amber-700 px-2 py-0.5">{t.seasonality.replace(/_/g, ' ')}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'portfolio' && portfolio && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { label: 'Brand Visibility', value: `${portfolio.brand_visibility_pct}%` },
            { label: 'Keyword Coverage', value: `${portfolio.keyword_coverage_pct}%` },
            { label: 'Category Coverage', value: `${portfolio.category_coverage_pct}%` },
            { label: 'Keyword Overlap', value: `${(portfolio.keyword_overlap_score * 100).toFixed(0)}%` },
            { label: 'Cannibalization Issues', value: portfolio.cannibalization_count.toString() },
            { label: 'Missing Opportunities', value: portfolio.missing_opportunities.toString() },
          ].map((m) => (
            <div key={m.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase text-slate-500">{m.label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button type="button" onClick={generateRecs} disabled={busy}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
              Generate Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <span className="text-xs font-medium uppercase text-sky-600">#{r.priority_rank} · {r.type.replace(/_/g, ' ')}</span>
                  <h3 className="font-medium text-slate-900 mt-1">{r.title}</h3>
                  {r.description && <p className="text-sm text-slate-600 mt-1">{r.description}</p>}
                  {r.evidence && <p className="text-xs text-slate-400 mt-1">{r.evidence}</p>}
                  {r.expected_impact && <p className="text-xs text-emerald-600 mt-1">{r.expected_impact}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold">{(r.confidence_score * 100).toFixed(0)}%</p>
                  {r.requires_approval && (
                    <button type="button" onClick={() => searchintelApi.approveRecommendation(r.id).then(refresh)}
                      className="mt-2 rounded bg-sky-100 px-3 py-1 text-xs font-medium text-sky-700 hover:bg-sky-200">
                      Approve
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
